import { useState } from 'react';
import { Button } from '@/shared/components/ui/Button';
import { Badge } from '@/shared/components/ui/Badge';
import { ManufacturerRulesCard } from './ManufacturerRulesCard';
import { OrderingTable } from './OrderingTable';
import { OrderBatchForm } from './OrderBatchForm';
import type { ManufacturerGroup as ManufacturerGroupType } from '@/features/frames-to-order/services/framesService';

interface ManufacturerGroupProps {
  group: ManufacturerGroupType;
  selectedFrameIds: string[];
  onSelectionChange: (frameIds: string[]) => void;
  onOrderSuccess: () => void;
}

export function ManufacturerGroup({
  group,
  selectedFrameIds,
  onSelectionChange,
  onOrderSuccess,
}: ManufacturerGroupProps) {
  const [isExpanded, setIsExpanded] = useState(true);
  const [isOrderFormOpen, setIsOrderFormOpen] = useState(false);

  const { manufacturer, frames, frameCount } = group;

  // Get selected frames for this manufacturer only
  const selectedInGroup = frames.filter((f) => selectedFrameIds.includes(f.id));
  const selectedCount = selectedInGroup.length;

  // Calculate min-order warning based on selected frames
  const hasMinOrderQty = manufacturer.min_order_qty !== null && manufacturer.min_order_qty > 0;
  const belowMinOrder = hasMinOrderQty && selectedCount > 0 && selectedCount < manufacturer.min_order_qty!;
  const framesNeeded = hasMinOrderQty && selectedCount > 0
    ? Math.max(0, manufacturer.min_order_qty! - selectedCount)
    : 0;

  const handleSelectAll = () => {
    const allFrameIds = frames.map((f) => f.id);
    if (selectedCount === frameCount) {
      // Deselect all in this group
      const remaining = selectedFrameIds.filter((id) => !allFrameIds.includes(id));
      onSelectionChange(remaining);
    } else {
      // Select all in this group
      const allIds = [...new Set([...selectedFrameIds, ...allFrameIds])];
      onSelectionChange(allIds);
    }
  };

  const handleOrderSelected = () => {
    if (selectedCount === 0) return;
    setIsOrderFormOpen(true);
  };

  const handleOrderAll = () => {
    // Select all frames in this group first
    const allFrameIds = frames.map((f) => f.id);
    const allIds = [...new Set([...selectedFrameIds, ...allFrameIds])];
    onSelectionChange(allIds);
    // Then open the form
    setIsOrderFormOpen(true);
  };

  const handleOrderSuccess = () => {
    // Clear selection for this manufacturer
    const frameIdsInGroup = frames.map((f) => f.id);
    const remaining = selectedFrameIds.filter((id) => !frameIdsInGroup.includes(id));
    onSelectionChange(remaining);
    onOrderSuccess();
  };

  return (
    <div className="bg-white rounded-lg shadow-md border border-gray-200 overflow-hidden mb-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-gray-50 to-gray-100 border-b border-gray-200">
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="w-full px-6 py-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
        >
          <div className="flex items-center gap-4">
            <div className="flex items-center justify-center w-8 h-8 bg-white rounded-full border-2 border-gray-300 text-gray-600 font-semibold">
              {isExpanded ? '−' : '+'}
            </div>
            <div className="text-left">
              <div className="flex items-center gap-3 mb-1">
                <h3 className="text-xl font-bold text-gray-900">{manufacturer.name}</h3>
                {hasMinOrderQty && (
                  <Badge variant="neutral" size="sm">
                    Min Order: {manufacturer.min_order_qty} frames
                  </Badge>
                )}
                {belowMinOrder && (
                  <Badge variant="warning" size="sm">
                    Below minimum
                  </Badge>
                )}
              </div>
              <p className="text-sm text-gray-600">
                {frameCount} frame{frameCount !== 1 ? 's' : ''} to order
                {selectedCount > 0 && (
                  <span className="ml-2 text-blue-600 font-medium">
                    ({selectedCount} selected)
                  </span>
                )}
              </p>
              {belowMinOrder && framesNeeded > 0 && (
                <p className="text-xs text-yellow-700 mt-1">
                  ⚠️ Need {framesNeeded} more frame{framesNeeded !== 1 ? 's' : ''} to reach minimum
                </p>
              )}
            </div>
          </div>

          <div className="text-sm text-gray-500">
            {isExpanded ? 'Click to collapse' : 'Click to expand'}
          </div>
        </button>
      </div>

      {/* Content */}
      {isExpanded && (
        <div className="p-6">
          {/* Manufacturer Rules Card */}
          <ManufacturerRulesCard
            manufacturer={manufacturer}
            selectedFrameCount={selectedCount}
            totalFrameCount={frameCount}
          />

          {/* Action Buttons */}
          <div className="flex items-center justify-between mb-4">
            <Button
              variant="secondary"
              size="sm"
              onClick={handleSelectAll}
            >
              {selectedCount === frameCount ? 'Deselect All' : 'Select All'}
            </Button>

            <div className="flex gap-2">
              <Button
                variant="primary"
                size="sm"
                onClick={handleOrderSelected}
                disabled={selectedCount === 0}
              >
                Order Selected ({selectedCount})
              </Button>
              <Button
                variant="primary"
                size="sm"
                onClick={handleOrderAll}
              >
                Order All ({frameCount})
              </Button>
            </div>
          </div>

          {/* Frames Table */}
          <OrderingTable
            frames={frames}
            selectedFrameIds={selectedFrameIds}
            onSelectionChange={onSelectionChange}
          />
        </div>
      )}

      {/* Order Batch Form Modal */}
      <OrderBatchForm
        isOpen={isOrderFormOpen}
        onClose={() => setIsOrderFormOpen(false)}
        manufacturer={manufacturer}
        frames={selectedInGroup}
        onSuccess={handleOrderSuccess}
      />
    </div>
  );
}
